// Freigabelauf einer Mail — wie im Event Manager.
//
// Beantwortet die Frage „Wer hat wann was entschieden?": Jeder Schritt vom
// Anlegen bis zum Versand mit Person, Zeitpunkt und Begründung — und was als
// Nächstes ansteht.
import {
  AlertTriangle, Archive, ArchiveRestore, CalendarClock, CheckCircle2, Circle, Clock, FileEdit, FilePlus2,
  MailCheck, MessageCircleQuestion, Search, Send, Trash2, Undo2, XCircle, type LucideIcon,
} from 'lucide-react';
import { aktionText, standVon, type Stand } from '../../lib/freigabe';
import { formatDateTime } from '../../lib/format';
import type { Campaign, HistoryEntry } from '../../lib/supabase';
import './Freigabelauf.css';

type Ton = 'neutral' | 'info' | 'warning' | 'success' | 'danger' | 'muted';

const SCHRITT: Record<string, { Icon: LucideIcon; ton: Ton }> = {
  angelegt: { Icon: FilePlus2, ton: 'neutral' },
  eingereicht: { Icon: Send, ton: 'info' },
  erneut_eingereicht: { Icon: Send, ton: 'info' },
  zurueckgezogen: { Icon: Undo2, ton: 'neutral' },
  in_pruefung: { Icon: Search, ton: 'warning' },
  rueckfrage: { Icon: MessageCircleQuestion, ton: 'warning' },
  freigegeben: { Icon: CheckCircle2, ton: 'success' },
  abgelehnt: { Icon: XCircle, ton: 'danger' },
  versand_gestartet: { Icon: Send, ton: 'info' },
  geplant: { Icon: CalendarClock, ton: 'info' },
  versendet: { Icon: MailCheck, ton: 'success' },
  fehler: { Icon: AlertTriangle, ton: 'danger' },
  archiviert: { Icon: Archive, ton: 'muted' },
  entwurf: { Icon: FileEdit, ton: 'neutral' },
  geloescht: { Icon: Trash2, ton: 'muted' },
  wiederhergestellt: { Icon: ArchiveRestore, ton: 'neutral' },
};

/** Was passiert als Nächstes — und wer ist dran. */
function ausblick(stand: Stand, c: Pick<Campaign, 'scheduled_at' | 'desired_send_at'>, sicht: 'autor' | 'redaktion'): string | null {
  switch (stand) {
    case 'entwurf': return 'Noch nicht eingereicht — erst mit dem Einreichen schaut die Redaktion darauf.';
    case 'rueckfrage': return sicht === 'redaktion'
      ? 'Wartet auf die Antwort der Person — danach reicht sie die Mail erneut ein.'
      : 'Die Redaktion wartet auf Dich: Mail anpassen und erneut einreichen.';
    case 'eingereicht': return 'Wartet auf die Redaktion. Sie nimmt die Mail in Prüfung und gibt frei oder fragt nach.';
    case 'in_pruefung': return 'Die Redaktion prüft gerade. Als Nächstes folgt Freigabe, Rückfrage oder Ablehnung.';
    case 'freigegeben': return 'Freigegeben — der Versand wird gerade eingeplant.';
    case 'geplant': return c.scheduled_at ? `Geht am ${formatDateTime(c.scheduled_at)} raus.` : 'Für den Versand eingeplant.';
    case 'wird_versendet': return 'Brevo verschickt die Mail gerade.';
    case 'fehler': return 'Beim Versand ist etwas schiefgegangen — die Redaktion kümmert sich darum.';
    default: return null;
  }
}

type Props = {
  campaign: Pick<Campaign, 'status' | 'review_state' | 'scheduled_at' | 'desired_send_at' | 'created_at' | 'created_by_name'>;
  verlauf: HistoryEntry[];
  laedt?: boolean;
  sicht?: 'autor' | 'redaktion';
};

export function Freigabelauf({ campaign, verlauf, laedt, sicht = 'autor' }: Props) {
  const stand = standVon(campaign);
  const naechstes = ausblick(stand, campaign, sicht);
  const hatAnlage = verlauf.some((h) => h.action === 'angelegt');

  return (
    <div className="card" data-tour="meine-verlauf">
      <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.75rem' }}>
        <Clock size={16} aria-hidden="true" /> Verlauf
      </h3>
      {laedt ? (
        <div className="loading-spinner" style={{ margin: '1rem auto' }} role="status" aria-label="Verlauf wird geladen" />
      ) : (
        <ol className="fl-liste">
          {!hatAnlage && (
            <li className="fl-schritt neutral">
              <span className="fl-punkt"><FilePlus2 size={14} /></span>
              <div className="fl-text">
                <strong>Entwurf angelegt</strong>
                <span className="fl-meta">{campaign.created_by_name ?? 'Jemand'} · {formatDateTime(campaign.created_at)}</span>
              </div>
            </li>
          )}
          {verlauf.map((h) => {
            const s = SCHRITT[h.action] ?? { Icon: Circle, ton: 'neutral' as Ton };
            return (
              <li key={h.id} className={`fl-schritt ${s.ton}`}>
                <span className="fl-punkt"><s.Icon size={14} /></span>
                <div className="fl-text">
                  <strong>{aktionText(h)}</strong>
                  <span className="fl-meta">{h.actor_name ?? 'Mailtool'} · {formatDateTime(h.created_at)}</span>
                  {h.message && <p className="fl-grund">{h.message}</p>}
                </div>
              </li>
            );
          })}
          {naechstes && (
            <li className="fl-schritt offen">
              <span className="fl-punkt"><Circle size={12} /></span>
              <div className="fl-text"><span className="fl-ausblick">{naechstes}</span></div>
            </li>
          )}
        </ol>
      )}
    </div>
  );
}
